import React from 'react';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Hamburger Icon Component
const HamburgerIcon = ({ open, ...props }) => (
  <motion.div
    className="space-y-1.5"
    animate={open ? 'open' : 'closed'}
    {...props}
  >
    <motion.span
      className="block h-0.5 w-6 bg-[#1a1a1a]"
      variants={{
        closed: { rotate: 0, y: 0 },
        open: { rotate: 45, y: 8 }, // Adjusted y for better centering
      }}
    />
    <motion.span
      className="block h-0.5 w-6 bg-[#1a1a1a]"
      variants={{
        closed: { opacity: 1 },
        open: { opacity: 0 },
      }}
    />
    <motion.span
      className="block h-0.5 w-6 bg-[#1a1a1a]"
      variants={{
        closed: { rotate: 0, y: 0 },
        open: { rotate: -45, y: -8 }, // Adjusted y for better centering
      }}
    />
  </motion.div>
);

export default function Top() {
  const { pathname } = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Handle scroll effect for navbar background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = pathname === "/" ? (
    <>
      <a href="#benefits" className="text-[#1a1a1a] font-medium hover:text-[#ca7751] transition-colors duration-300" onClick={() => setIsMobileMenuOpen(false)}>Benefits</a>
      <a href="#process" className="text-[#1a1a1a] font-medium hover:text-[#ca7751] transition-colors duration-300" onClick={() => setIsMobileMenuOpen(false)}>How It Works</a>
      <a href="#features" className="text-[#1a1a1a] font-medium hover:text-[#ca7751] transition-colors duration-300" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
      <Link to="/Login" className="px-8 py-3 bg-[#ca7751] text-white rounded-lg font-semibold hover:bg-[#b66642] transition-colors duration-300" onClick={() => setIsMobileMenuOpen(false)}>Login</Link>
    </>
  ) : (
    <Link to="/Register" className="px-8 py-3 bg-[#ca7751] text-white rounded-lg font-semibold hover:bg-[#b66642] transition-colors duration-300" onClick={() => setIsMobileMenuOpen(false)}>Get Started</Link>
  );

  // Framer Motion Variants
  const navVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 120, damping: 20, delay: 0.2 },
    },
  };

  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.2, when: "afterChildren" },
    },
    open: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, when: "beforeChildren", staggerChildren: 0.05 },
    },
  };

  const mobileLinkVariants = {
    closed: { opacity: 0, y: -10 },
    open: { opacity: 1, y: 0 },
  };

  return (
    <motion.nav
      className={`py-5 px-[7%] flex justify-between items-center fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen ? 'bg-white shadow-md' : 'bg-white' // Changed to transparent initially
      }`}
      variants={navVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Logo */}
      <Link to="/" className="h-[40px] w-[170px] z-10"> {/* Ensure logo is above mobile menu bg */}
        <img src={'https://d3qvi06vol9hrb.cloudfront.net/static/media/logo-img.e08e272c66f1e35ac87c.png'} alt="Vasstr" className="h-[40px] w-auto md:p-0 p-2 max-w-[200px]" />
      </Link>

      {/* Desktop Menu */}
      <div className="hidden md:flex gap-8 items-center"> {/* Adjusted gap for desktop */}
        {navLinks}
      </div>

      {/* Hamburger Button - MD and below */}
      <div className="md:hidden z-10"> {/* Ensure hamburger is above mobile menu bg */}
        <button
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          className="p-2"
        >
          <HamburgerIcon open={isMobileMenuOpen} />
        </button>
      </div>

      {/* Mobile Menu with Framer Motion */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden absolute top-0 left-0 w-full h-screen bg-white pt-24 pb-8 px-[7%] flex flex-col items-center gap-6 overflow-y-auto"
            variants={mobileMenuVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            {React.Children.map(navLinks.props.children || navLinks, (child, index) => (
               child && React.isValidElement(child) ? (
                <motion.div key={index} variants={mobileLinkVariants} className="w-full text-center">
                  {React.cloneElement(child, {
                    className: `${child.props.className} block w-full py-3 text-lg`, // Ensure full width and larger text
                    onClick: () => {
                      if (child.props.onClick) child.props.onClick(); // Call original onClick if it exists
                      setIsMobileMenuOpen(false); // Always close menu
                    }
                  })}
                </motion.div>
              ) : null
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}